// Copyright (c) Microsoft Corporation. All rights reserved.

import * as microsoftTeams from "@microsoft/teams-js";

import Constants from "../misc/WegoConstants";
import { PublicClientApplication } from "@azure/msal-browser";
import WegoAuthUser from "./WegoAuthUser";
import WegoConfiguration from "../WegoConfiguration";

/*
** This class supports both Teams and naked browser scenarios.
** When running inside Teams, we get the token from them (SSO).  
** When in a naked browser, we get the token from MSAL direct.
*/
export default class WegoAuth {
    public msalObject?: PublicClientApplication;
    private authToken?: string;
    private teamsMode: boolean;
    private wegoConfig: WegoConfiguration;

    constructor(teamsMode: boolean, config: WegoConfiguration) {
        this.teamsMode = teamsMode;
        this.wegoConfig = config;

        if (!teamsMode) {
            this.msalObject = new PublicClientApplication(this.getMsalConfig(config));
        }
    }

    private getMsalConfig(config: WegoConfiguration) {
        let rawContants = Constants.MsalConfig;

        rawContants.auth.clientId = config.aadAppId;
        rawContants.auth.redirectUri = config.clientUrl + "/AuthEnd";
        console.log(rawContants);
        return rawContants;
    }

    getToken(): Promise<string> {
        return new Promise((resolve, reject) => {
            switch (this.teamsMode) {
                case false:
                    let accessTokenRequest = {
                        account: this.msalObject!.getAllAccounts()[0],
                        scopes: [this.getScope()]
                    }

                    this.msalObject!.acquireTokenSilent(accessTokenRequest)
                        .then(accessTokenResponse => {
                            this.authToken = accessTokenResponse.accessToken;
                            resolve(accessTokenResponse.accessToken);
                        }).catch(error => {
                            console.log(error);
                            reject(error);
                        });
                    return;
                case true:
                    microsoftTeams.authentication.getAuthToken({
                        successCallback: result => {
                            console.warn("got auth token: " + result);
                            this.authToken = result;
                            resolve(result);
                        },
                        failureCallback: reason => {
                            console.warn("failed to get auth token: " + reason);
                            reject(reason);
                        }
                    });
            }
        });
    }

    getUser(): Promise<WegoAuthUser> {
        return new Promise((resolve, reject) => {
            // This cached token could be expired, but we don"t care as we only want the name.
            if (this.authToken) {
                resolve(this.parseTokenToUser(this.authToken));
            } else {
                this.getToken()
                    .then(token => {
                        resolve(this.parseTokenToUser(token));
                    })
                    .catch(reason => {
                        reject(reason);
                    });
            }
        });
    }

    private parseTokenToUser(token: string): WegoAuthUser {
        // This works for both Teams and Browser hosts.
        var base64Url = token.split(".")[1];
        var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        var parsedToken = JSON.parse(window.atob(base64));
        return new WegoAuthUser(parsedToken.oid, parsedToken.name);
    }

    getScope(): string {
        const httpUrl = this.wegoConfig.clientUrl;
        const httpLocation = httpUrl!.indexOf("//");
        const finishedScope = "api:" + httpUrl!.slice(httpLocation) + "/" + this.wegoConfig.aadAppId + "/access_as_user";
        console.log(finishedScope);
        return finishedScope;
    }
}